import React, { useState } from 'react';
import { useTimeTrackerStore } from '@/stores/time-tracker';
import { formatDuration } from './timeTrackerUtils';
import type { TimeTrackerProject } from '@/types/pm-types';
import { pmTheme } from './pmTheme';

interface TimerBarProps {
  projects: TimeTrackerProject[];
  defaultBillable?: boolean;
  accentColor?: string;
}

export const TimerBar: React.FC<TimerBarProps> = ({
  projects,
  defaultBillable = true,
  accentColor = '#10b981',
}) => {
  const timer = useTimeTrackerStore((s) => s.timer);
  const startTimer = useTimeTrackerStore((s) => s.startTimer);
  const stopTimer = useTimeTrackerStore((s) => s.stopTimer);
  const updateTimerDescription = useTimeTrackerStore((s) => s.updateTimerDescription);
  const updateTimerProject = useTimeTrackerStore((s) => s.updateTimerProject);
  const updateTimerBillable = useTimeTrackerStore((s) => s.updateTimerBillable);
  const addManualEntry = useTimeTrackerStore((s) => s.addManualEntry);

  const [isManualMode, setIsManualMode] = useState(false);
  const [manualStart, setManualStart] = useState('');
  const [manualEnd, setManualEnd] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualProjectId, setManualProjectId] = useState('');
  const [manualBillable, setManualBillable] = useState(defaultBillable);

  // Timer ticking is handled globally by the store (time-tracker-store.ts)
  // so it continues running even when this component is unmounted.

  const handleStart = () => {
    startTimer({
      description: timer.description,
      projectId: timer.projectId ?? undefined,
      billable: timer.billable || defaultBillable,
    });
  };

  const handleStop = () => {
    stopTimer();
  };

  const handleManualAdd = () => {
    if (!manualStart || !manualEnd) return;
    const start = new Date(manualStart);
    const end = new Date(manualEnd);
    if (end <= start) return;
    const duration = Math.floor((end.getTime() - start.getTime()) / 1000);
    addManualEntry({
      description: manualDescription,
      projectId: manualProjectId || undefined,
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      duration,
      billable: manualBillable,
      tags: [],
      date: manualStart.slice(0, 10),
    });
    setManualDescription('');
    setManualStart('');
    setManualEnd('');
  };

  const selectedProject = projects.find((p) => p.id === (timer.projectId || ''));

  if (isManualMode) {
    return (
      <div data-testid="timer-bar" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${pmTheme.border}`, background: pmTheme.surfaceSubtle }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="text"
            placeholder="What are you working on?"
            value={manualDescription}
            onChange={(e) => setManualDescription(e.target.value)}
            style={{ flex: 1, padding: '6px 10px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 14, outline: 'none', color: pmTheme.text, background: pmTheme.surface }}
          />
          <select
            value={manualProjectId}
            onChange={(e) => setManualProjectId(e.target.value)}
            style={{ padding: '6px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 13, color: pmTheme.text, background: pmTheme.surface }}
          >
            <option value="">No project</option>
            {projects.filter((p) => !p.archived).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="datetime-local" value={manualStart} onChange={(e) => setManualStart(e.target.value)} style={{ padding: '4px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 13, color: pmTheme.text, background: pmTheme.surface }} />
          <span style={{ color: pmTheme.textSubtle }}>to</span>
          <input type="datetime-local" value={manualEnd} onChange={(e) => setManualEnd(e.target.value)} style={{ padding: '4px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 13, color: pmTheme.text, background: pmTheme.surface }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: pmTheme.textMuted, cursor: 'pointer' }}>
            <input type="checkbox" checked={manualBillable} onChange={(e) => setManualBillable(e.target.checked)} />
            Billable
          </label>
          <button onClick={handleManualAdd} style={{ padding: '6px 16px', background: accentColor, color: pmTheme.textInverse, border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
            Add
          </button>
          <button onClick={() => setIsManualMode(false)} style={{ padding: '6px 12px', background: 'transparent', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 13, cursor: 'pointer', color: pmTheme.textMuted }}>
            Timer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="timer-bar" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${pmTheme.border}`, background: timer.isRunning ? pmTheme.successSoft : pmTheme.surfaceSubtle }}>
      <input
        type="text"
        data-testid="timer-description"
        placeholder="What are you working on?"
        value={timer.description}
        onChange={(e) => updateTimerDescription(e.target.value)}
        style={{ flex: 1, padding: '8px 12px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 14, outline: 'none', background: pmTheme.surface, color: pmTheme.text }}
      />

      <select
        value={timer.projectId || ''}
        onChange={(e) => updateTimerProject(e.target.value || null)}
        style={{ padding: '8px 10px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 13, minWidth: 130, background: pmTheme.surface, color: pmTheme.text }}
      >
        <option value="">No project</option>
        {projects.filter((p) => !p.archived).map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>

      {selectedProject && (
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: selectedProject.color, flexShrink: 0 }} />
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: pmTheme.textMuted, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <input
          type="checkbox"
          checked={timer.billable}
          onChange={(e) => updateTimerBillable(e.target.checked)}
        />
        $
      </label>

      <div style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: timer.isRunning ? accentColor : pmTheme.text, minWidth: 90, textAlign: 'center' }}>
        {formatDuration(timer.elapsedSeconds)}
      </div>

      <button
        data-testid="timer-start-button"
        onClick={timer.isRunning ? handleStop : handleStart}
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: 'none',
          background: timer.isRunning ? '#ef4444' : accentColor,
          color: pmTheme.textInverse,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          flexShrink: 0,
          transition: 'background 0.2s',
        }}
        title={timer.isRunning ? 'Stop timer' : 'Start timer'}
      >
        {timer.isRunning ? '■' : '▶'}
      </button>

      <button
        onClick={() => setIsManualMode(true)}
        style={{ padding: '6px 10px', background: 'transparent', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', color: pmTheme.textMuted, whiteSpace: 'nowrap' }}
        title="Switch to manual entry"
      >
        Manual
      </button>
    </div>
  );
};

TimerBar.displayName = 'TimerBar';
